import { Component, Input, OnChanges, OnInit, Output, SimpleChanges, EventEmitter, TemplateRef } from '@angular/core';
import { DEFAULT_PORTFOLIO_ID } from 'app/shared/helper/navigation-data-constants';
import { UserPreferencesPortfolioService } from 'app/shared/services/data-services/user-preferences-portfolio/user-preferences-portfolio.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NavigationService } from '../../services/data-services/navigation/navigation.service';
import { removeAccentsAndDiacritics } from 'app/shared/helper/common.helper';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { getValidatorConfig, isValidPortfolioName } from 'app/shared/helper/validation.helper';
import { openSuccess } from 'app/shared/helper/notification-message.helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { UserPortfolioPreferencesService } from 'app/shared/services/user-preferences/user-portfolio-preferences.service';

@Component({
    selector: 'app-header-filter',
    templateUrl: './header-filter.component.html',
    styleUrls: ['./header-filter.component.scss'],
    standalone: false
})
export class HeaderFilterComponent implements OnInit, OnChanges {

  @Input() selectedItem;
  @Input() filteredItem;
  @Input() config;
  @Input() theme?;
  @Input() portfolioLevel;
  @Input() defaultPortfolio;
  @Input() disableCreate?: boolean

  @Output() unpin = new EventEmitter();
  @Output() createNewPortfolio = new EventEmitter();
  @Output() changePlantSelection = new EventEmitter();
  @Output() changeTurbineSelection = new EventEmitter();

  public allPowerplantsSelected: boolean;
  public allTurbSelected: boolean
  public countries: object;
  public inputsSortOptions: string[] = ['HEADER.A_Z', 'HEADER.Z_A'];
  public inputsShowOptions: { values: string[], names: string[]};
  public selectedInputLabel: string;
  public modalRef: BsModalRef;
  public shownPopover: boolean;
  public portFolioName: string;
  public defaultId: string;
  public statusSelected: string;
  public signalType: string;
  public filterByName = '';
  public formUser: UntypedFormGroup;
  public deviceGroupList: {
    id: string,
    selected: boolean,
    park_id: string,
    name: string,
    turbines: string[]
  }[];



  public get allPlantsSelected() {
    return this.filteredItem?.powerplants.every(plant => plant.selected);
  }

  public get nonePlantsSelected() {
    return this.filteredItem?.powerplants.every(plant => !plant.selected);
  }

  public get allTurbinesSelected() {
    return this.filteredItem?.turbines.every(turb => turb.selected);
  }

  constructor(
    private modalService: BsModalService,
    private navigationService: NavigationService,
    private translateService: TranslateService,
    private snackBar: MatSnackBar,
    private userPortfolioService: UserPreferencesPortfolioService,
    private userPreferencesService: UserPortfolioPreferencesService,
    private formBuilder: UntypedFormBuilder,
  ) { }

  public ngOnInit(): void {
    this.selectedInputLabel = this.inputsSortOptions[0];
    this.shownPopover = false;
    this.defaultId = DEFAULT_PORTFOLIO_ID;
    this.initiateForm();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if(changes?.filteredItem?.currentValue) {
      if (this.portfolioLevel) {
        this.initPortfolioSelection();
      } else {
        this.initPowerplantSelection();
      }
    }
    if(changes?.selectedItem?.currentValue?.groups && !this.portfolioLevel) {
      this.deviceGroupList = changes.selectedItem.currentValue.groups;
      this.initGroupSelection();
    }

    if(changes.config) {
      this.inputsShowOptions = {
        names: this.config.options.map(option => `HEADER.${option.label.toUpperCase().replace(' ', '_')}`),
        values: this.config.options.map(option => option.value)
      }
      const index = this.inputsShowOptions.values.indexOf(this.config.signalType) || 0;
      this.statusSelected = this.inputsShowOptions.names[index];
      this.signalType = this.config.signalType;
    }
  }

  public initPortfolioSelection() {
    this.countries = {};
    this.sortPlantsBy({ index: 0 });
    this.filteredItem?.powerplants.forEach(plant => {
      this.countries[plant.countryCode] = true;
      plant.selected = this.config?.selected?.length ? this.config?.selected.includes(plant.id) : plant.selected;

      if (this.navigationService.isParkDeactivated(plant.id)) {
        plant.selected = false;
      }
    });
    this.allPowerplantsSelected = this.allPlantsSelected;
    for(const code in this.countries) {
      this.countries[code] = this.isCountrySelected(code);
    }
    this.config.selected = this.filteredItem?.powerplants.filter(plant => plant.selected).map(plant => plant.id);
  }

  public initPowerplantSelection() {
    this.filteredItem?.turbines.sort((a, b) => a.code.localeCompare(b.code, 'en', {numeric: true})).forEach(turb => {
      turb.selected = this.config?.selected?.length ? this.config?.selected.includes(turb.id) : turb.selected;

      if (this.navigationService.isTurbineDeactivated(turb.id)) {
        turb.selected = false;
      }
    });
    this.allTurbSelected = this.allTurbinesSelected;
    this.config.selected = this.filteredItem?.turbines.filter(turb => turb.selected).map(turb => turb.id);
  }

  public initGroupSelection() {
    if(this.deviceGroupList && this.deviceGroupList?.length !== 0 && this.filteredItem?.turbines) {
      const selectedTurbineIdList = this.filteredItem.turbines.filter(i => i.selected).map(i => i.id);
      this.deviceGroupList.forEach(g => {
        g.selected = g.turbines.every(i => selectedTurbineIdList.includes(i));
      });
    }
  }


  public setPlantsSelected() {
    this.config.selected = this.filteredItem?.powerplants.filter(plant => plant.selected).map(plant => plant.id);
    this.changePlantSelection.emit();
  }

  public setTurbinesSelected() {
    this.config.selected = this.filteredItem?.turbines.filter(turb => turb.selected).map(turb => turb.id)
    this.changeTurbineSelection.emit();
  }

  public selectAllPlants() {
    this.allPowerplantsSelected = !this.allPowerplantsSelected;
    this.filteredItem?.powerplants.forEach(plant => {
      plant.selected = this.allPowerplantsSelected;
      if (this.navigationService.isParkDeactivated(plant.id)) {
        plant.selected = false;
      }
    });
    for(const code in this.countries) {
      this.countries[code] = this.isCountrySelected(code);
    }
    this.setPlantsSelected();
  }

  public selectAllTurbines() {
    this.allTurbSelected = !this.allTurbSelected;
    this.filteredItem?.turbines.forEach(turb => {
      turb.selected = this.allTurbSelected;
      if (this.navigationService.isTurbineDeactivated(turb.id)) {
        turb.selected = false;
      }
    });
    this.deviceGroupList?.forEach(group => {
      group.selected = this.allTurbSelected;
    });
    this.setTurbinesSelected();
  }

  public selectTurbinesGroup(group) {
    this.deviceGroupList?.forEach(g => {
       if(g.id === group.id) {
        g.selected = !group.selected;
      }
    })
    let selectedTurbinesInGroups = this.deviceGroupList.filter(g => g.selected).flatMap(g => g.turbines);
    
    this.filteredItem?.turbines.forEach(turbine => {
      turbine.selected = !!selectedTurbinesInGroups.includes(turbine.id);
    });
    this.allTurbSelected = this.filteredItem?.turbines.every(turbine => turbine.selected);
    this.setTurbinesSelected();
  }


  public selectCountry(country, event) {
    event.stopPropagation();
    this.countries[country.key] = !country.value;
    this.filteredItem?.powerplants.forEach(plant => {
      if (plant.countryCode === country.key) {
        plant.selected = !country.value;
      }
    });
    this.allPowerplantsSelected = this.allPlantsSelected;
    this.setPlantsSelected();
  }

  public selectPlant(plant, event) {
    event.stopPropagation();
    this.filteredItem?.powerplants.forEach(poverplant => {
      if (poverplant.powerPlantCode === plant.powerPlantCode) {
        poverplant.selected = !poverplant.selected;
      }
    });
    this.countries[plant.countryCode] = this.isCountrySelected(plant.countryCode);
    this.allPowerplantsSelected = this.allPlantsSelected;
    this.setPlantsSelected();
  }

  public selectTurbine(item, event) {
    setTimeout(() => {
      event.stopPropagation();
      this.filteredItem?.turbines.forEach(turb => {
        if (turb.id === item.id) {
          turb.selected = !turb.selected;
        }
      });
      if(this.deviceGroupList?.length !== 0 && this.filteredItem?.turbines) {
        const selectedTurbineIdList = this.filteredItem.turbines.filter(i => i.selected).map(i => i.id);
        this.deviceGroupList.forEach(g => {
          g.selected = g.turbines.every(i => selectedTurbineIdList.includes(i));
        });
      }
      this.allTurbSelected = this.allTurbinesSelected;
      this.setTurbinesSelected();
    }, 5)
  }

  public calcLabelClasses(countryCode) {
    return 'font-size-small font-weight-bold text-overflow-ellipsis ' + (this.isCountryDeactivated(countryCode) ? 'text-gray' : 'text-primary');
    }

  public isParkDeactivated(parkId) {
    return this.navigationService.isParkDeactivated(parkId);
  }

  public isTurbineDeactivated(turbineId) {
    return this.navigationService.isTurbineDeactivated(turbineId);
  }

  public isCountryDeactivated(code) {
    return this.navigationService.isCountryDeactivated(code);
  }

  public isCountrySelected(code) {
    return this.filteredItem.powerplants
    .filter(poverplant => poverplant.countryCode === code)
    .every(poverplant => poverplant.selected)
  }

  public sortPlantsBy(selectedValue) {
    this.selectedInputLabel = this.inputsSortOptions[selectedValue.index];
    selectedValue.index ?
      this.filteredItem?.powerplants.sort((a, b) => b.powerPlantCode.localeCompare(a.powerPlantCode)) :
      this.filteredItem?.powerplants.sort((a, b) => a.powerPlantCode.localeCompare(b.powerPlantCode));
  }

  public pinToggle() {
    this.config.sticky = !this.config.sticky;
    if (!this.config.sticky) {
      this.unpin.emit()
    }
  }

  public saveNewPortfolio() {
    const plants = this.filteredItem?.powerplants.filter(plant => plant.selected).map(plant => plant.id);
    this.createNewPortfolio.emit(plants)
  }

  public modalServiceWindow(modalTemplate: TemplateRef<any>) {
    this.shownPopover = false;
    this.modalRef = this.modalService.show(modalTemplate, {
      class: `modal-dialog-centered`, backdrop: 'static', keyboard: false
    });
  }

  public hideModalServiceWindow() {
    this.modalRef.hide();
  }

  public deletePortfolio() {
    this.userPortfolioService.deleteCustomPorfolio({portfolio: this.filteredItem.id}).subscribe(() => {
      this.hideModalServiceWindow();
      this.navigationService.redirectToPortfolio(this.defaultPortfolio.id, this.defaultPortfolio.powerplants.map(p => p.id));
      this.showSuccessMessage('HEADER.PORTFOLIO_DELETED');
    })
  }

  private showSuccessMessage(msgKey) {
    const message = this.translateService.instant(msgKey);
    openSuccess(this.snackBar, message);
  }

  public filterByNameEvent(event) {
    this.filterByName = event.target.value.trim().toLowerCase();
  }

  public showFilteredTurbine(name) {
    const processedName = removeAccentsAndDiacritics(name.toLowerCase());
    const processedSearch = removeAccentsAndDiacritics(this.filterByName);
    return processedName.includes(processedSearch);
  }

  public changeStatus({option, index}) {
    this.statusSelected = option;
    this.signalType = this.inputsShowOptions.values[index];
  }

  initiateForm() {
    this.formUser = this.formBuilder.group({
      portFolioName: ['', getValidatorConfig('portFolioName', this.userPreferencesService)],
    });
  }

  getValidationPortfolioName() {
    return isValidPortfolioName(this.formUser.get('portFolioName'));
  }

}
